import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, ButtonBase } from '@mui/material';
import Add from '@mui/icons-material/Add';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faHeart } from '@fortawesome/free-solid-svg-icons';

import { BookmarkIcon } from '../../BookmarkIcon';
import { getFormatViews, getGameIcon } from '../../../helpers';
import { Video } from '../../../types';
import styles from './style';

interface ActionsProps {
  // Read only on mobile: every action opens the popup to get the app.
  openShortModal: () => void;
  inTiktok: boolean;
  video: Video;
}

// Column on the right of the video, like the app and TikTok: the creator, the actions
// (heart, comment, favorite, share) and the game at the bottom.
const Actions = React.memo((props: ActionsProps) => {
  const {
    openShortModal,
    inTiktok,
    video,
  } = props;
  const { t } = useTranslation();

  const avatarUrl = useMemo(
    () => (video.user && video.user.avatar ? `${process.env.REACT_APP_AWS_CLOUDFRONT_URL}${video.user.avatar}` : ''),
    [video.user],
  );
  const gameIcon = useMemo(() => (video.gameName ? getGameIcon(video.gameName) : ''), [video.gameName]);

  return (
    <Box sx={inTiktok ? styles.columnTiktok : styles.column}>
      <ButtonBase
        disableRipple
        onClick={openShortModal}
        aria-label={video.user ? video.user.username : ''}
        sx={styles.creator}
      >
        <Avatar alt="" src={avatarUrl} sx={styles.avatar} />
        <Box component="span" sx={styles.follow}>
          <Add />
        </Box>
      </ButtonBase>
      <ButtonBase disableRipple onClick={openShortModal} aria-label={t('admin.feed.like')} sx={styles.action}>
        <FontAwesomeIcon icon={faHeart} />
        <Box component="span" sx={styles.count}>{getFormatViews(video.likes || 0)}</Box>
      </ButtonBase>
      <ButtonBase disableRipple onClick={openShortModal} aria-label={t('admin.feed.comments')} sx={styles.action}>
        <FontAwesomeIcon icon={faCommentDots} />
        <Box component="span" sx={styles.count}>{getFormatViews(video.comments || 0)}</Box>
      </ButtonBase>
      <ButtonBase disableRipple onClick={openShortModal} aria-label={t('admin.feed.favorite')} sx={styles.action}>
        <BookmarkIcon fontSize="inherit" />
        <Box component="span" sx={styles.count}>{getFormatViews(video.favorites || 0)}</Box>
      </ButtonBase>
      <ButtonBase disableRipple onClick={openShortModal} aria-label={t('admin.feed.share')} sx={styles.action}>
        {/* Same arrow as the app (Ionicons arrow-redo), viewBox cut to the glyph. */}
        <svg viewBox="48 76 416 372" fill="currentColor" aria-hidden="true">
          <path d="M58.79 439.13A16 16 0 0 1 48 424c0-73.1 14.68-131.56 43.65-173.77 35-51 90.21-78.46 164.35-81.87V88a16 16 0 0 1 27.05-11.57l176 168a16 16 0 0 1 0 23.14l-176 168A16 16 0 0 1 256 424v-79.77c-45 1.36-79 8.65-106.07 22.64-29.25 15.12-50.46 37.71-73.32 67a16 16 0 0 1-17.82 5.28z" />
        </svg>
        <Box component="span" sx={styles.count}>{getFormatViews(video.shares || 0)}</Box>
      </ButtonBase>
      {gameIcon && (
        <ButtonBase disableRipple onClick={openShortModal} aria-label={video.gameName} sx={styles.gameButton}>
          <Box component="img" src={gameIcon} alt="" sx={styles.game} />
        </ButtonBase>
      )}
    </Box>
  );
});

export default Actions;
